import { db } from "../../firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit as firestoreLimit,
  startAfter,
  documentId,
} from "firebase/firestore";
import axios from "axios";
import Swal from "sweetalert2";
import * as types from "./FullLengthExamTypes";

// ===========================
// ADMIN - EXAM TEMPLATE CRUD
// ===========================

/**
 * Create a new Full-Length Exam template
 * @param {Object} examData - Exam configuration
 * @param {string} examData.name - Exam name
 * @param {Array} examData.questions - Array of embedded question objects (from Excel upload)
 * @param {boolean} examData.isActive - Whether exam is available to students
 */
export const createFullLengthExam = (examData) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.CREATE_EXAM_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      const examsRef = collection(db, "fullLengthExams");

      // Generate exam ID
      const q = query(examsRef, orderBy("examNumber", "desc"), firestoreLimit(1));
      const snapshot = await getDocs(q);

      let examNumber = 1;
      if (!snapshot.empty) {
        const highestExam = snapshot.docs[0].data();
        examNumber = (highestExam.examNumber || 0) + 1;
      }

      const examId = `EXAM-${String(examNumber).padStart(6, "0")}`;

      // Assign unique question IDs tied to this exam
      const questions = (examData.questions || []).map((q, index) => ({
        ...q,
        questionId: `${examId}-Q${String(index + 1).padStart(4, "0")}`,
      }));

      const questionCount = questions.length;

      // Build section configuration if provided
      let sections = null;
      let hasSections = false;
      let breakDurationSeconds = 0;
      let timeLimit;

      if (examData.sections && examData.sections.length >= 2) {
        hasSections = true;
        breakDurationSeconds = examData.breakDurationSeconds || 0;

        let runningIndex = 0;
        sections = examData.sections.map((s) => {
          const section = {
            sectionIndex: s.sectionIndex,
            name: s.name,
            questionCount: s.questionCount,
            timeLimitSeconds: s.timeLimitSeconds,
            questionStartIndex: runningIndex,
            questionEndIndex: runningIndex + s.questionCount,
          };
          runningIndex += s.questionCount;
          return section;
        });

        // Total time = sum of section times
        timeLimit = sections.reduce((sum, s) => sum + s.timeLimitSeconds, 0);
      } else {
        // Legacy: 66 seconds per question
        timeLimit = questionCount * 66;
      }

      const examDocument = {
        examId,
        examNumber,
        name: examData.name,
        questionCount,
        questions,
        timeLimit, // in seconds
        isActive: examData.isActive !== undefined ? examData.isActive : true,
        ...(hasSections && { hasSections, sections, breakDurationSeconds }),
        ...(examData.passages?.length > 0 && { passages: examData.passages }),
        createdBy: userHandle,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const examDocRef = doc(examsRef, examId);
      await setDoc(examDocRef, examDocument);

      const savedExam = {
        ...examDocument,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      dispatch({
        type: types.CREATE_EXAM_SUCCESS,
        payload: savedExam,
      });

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: `Full-Length Exam created with ${questionCount} questions`,
        confirmButtonColor: "#5e72e4",
        timer: 2000,
      });

      return { success: true, examId };
    } catch (error) {
      dispatch({
        type: types.CREATE_EXAM_FAILURE,
        payload: error.message,
      });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to create exam",
        confirmButtonColor: "#5e72e4",
      });

      return { success: false, error: error.message };
    }
  };
};

/**
 * Fetch Full-Length Exam list for students — lightweight (no questions array)
 */
export const fetchFullLengthExams = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: types.FETCH_EXAMS_REQUEST });

      const response = await axios.post("/fetchExamListForStudents");
      const exams = response.data.exams || [];

      dispatch({
        type: types.FETCH_EXAMS_SUCCESS,
        payload: {
          exams,
          append: false,
          lastVisible: null,
          hasMore: false,
        },
      });

      return { success: true, exams };
    } catch (error) {
      console.error("Error fetching full-length exams:", error);

      dispatch({
        type: types.FETCH_EXAMS_FAILURE,
        payload: error.response?.data?.error || error.message,
      });

      return { success: false, error: error.message };
    }
  };
};

/**
 * Fetch Full-Length Exams for admin — cached with attempt counts
 */
export const fetchFullLengthExamsCached = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: types.FETCH_EXAMS_REQUEST });

      const response = await axios.post("/fetchFullLengthExamsCached");
      const { exams, attemptCounts } = response.data;

      dispatch({
        type: types.FETCH_EXAMS_SUCCESS,
        payload: {
          exams: exams || [],
          append: false,
          lastVisible: null,
          hasMore: false,
        },
      });

      if (attemptCounts) {
        dispatch({
          type: types.FETCH_EXAM_ATTEMPT_COUNTS_SUCCESS,
          payload: attemptCounts,
        });
      }

      return { success: true, exams };
    } catch (error) {
      console.error("Error fetching cached exams:", error);

      dispatch({
        type: types.FETCH_EXAMS_FAILURE,
        payload: error.response?.data?.error || error.message,
      });

      return { success: false, error: error.message };
    }
  };
};

/**
 * Force rebuild exam cache (admin)
 */
export const rebuildExamCache = () => {
  return async () => {
    try {
      await axios.post("/rebuildExamCache");
      return { success: true };
    } catch (error) {
      console.error("Error rebuilding exam cache:", error);
      return { success: false, error: error.message };
    }
  };
};

/**
 * Fetch single Full-Length Exam by ID
 */
export const fetchFullLengthExam = (examId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: types.FETCH_EXAM_REQUEST });

      const examRef = doc(db, "fullLengthExams", examId);
      const examDoc = await getDoc(examRef);

      if (!examDoc.exists()) {
        throw new Error("Exam not found");
      }

      const examData = examDoc.data();
      const exam = {
        examId: examDoc.id,
        ...examData,
        createdAt: examData.createdAt?.toMillis?.() || examData.createdAt,
        updatedAt: examData.updatedAt?.toMillis?.() || examData.updatedAt,
      };

      dispatch({
        type: types.FETCH_EXAM_SUCCESS,
        payload: exam,
      });

      return { success: true, exam };
    } catch (error) {
      dispatch({
        type: types.FETCH_EXAM_FAILURE,
        payload: error.message,
      });

      return { success: false, error: error.message };
    }
  };
};

/**
 * Update Full-Length Exam template
 */
export const updateFullLengthExam = (examId, updates) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.UPDATE_EXAM_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      const examRef = doc(db, "fullLengthExams", examId);

      // Recalculate time limit if question count changed
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      if (updates.sections && updates.sections.length >= 2) {
        updateData.timeLimit = updates.sections.reduce(
          (sum, s) => sum + s.timeLimitSeconds,
          0
        );
      } else if (updates.questionCount && !updates.hasSections) {
        updateData.timeLimit = updates.questionCount * 66;
      }

      await updateDoc(examRef, updateData);

      // Fetch updated exam
      const examDoc = await getDoc(examRef);
      const examData = examDoc.data();

      const updatedExam = {
        examId,
        ...examData,
        createdAt: examData.createdAt?.toMillis?.() || examData.createdAt,
        updatedAt: new Date().toISOString(),
      };

      dispatch({
        type: types.UPDATE_EXAM_SUCCESS,
        payload: updatedExam,
      });

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Exam updated successfully",
        confirmButtonColor: "#5e72e4",
        timer: 2000,
      });

      return { success: true };
    } catch (error) {
      dispatch({
        type: types.UPDATE_EXAM_FAILURE,
        payload: error.message,
      });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to update exam",
        confirmButtonColor: "#5e72e4",
      });

      return { success: false, error: error.message };
    }
  };
};

/**
 * Delete Full-Length Exam template
 */
export const deleteFullLengthExam = (examId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.DELETE_EXAM_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      const examRef = doc(db, "fullLengthExams", examId);
      await deleteDoc(examRef);

      dispatch({
        type: types.DELETE_EXAM_SUCCESS,
        payload: examId,
      });

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Exam deleted successfully",
        confirmButtonColor: "#5e72e4",
        timer: 2000,
      });

      return { success: true };
    } catch (error) {
      dispatch({
        type: types.DELETE_EXAM_FAILURE,
        payload: error.message,
      });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to delete exam",
        confirmButtonColor: "#5e72e4",
      });

      return { success: false, error: error.message };
    }
  };
};

// fetchExamAttemptCounts is now included in fetchFullLengthExamsCached response

/**
 * Archive a Full-Length Exam (soft delete)
 * Sets isArchived: true and isActive: false to preserve student data
 */
export const archiveFullLengthExam = (examId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.ARCHIVE_EXAM_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      const examRef = doc(db, "fullLengthExams", examId);
      await updateDoc(examRef, {
        isArchived: true,
        isActive: false,
        updatedAt: serverTimestamp(),
      });

      dispatch({
        type: types.ARCHIVE_EXAM_SUCCESS,
        payload: examId,
      });

      Swal.fire({
        icon: "success",
        title: "Archived!",
        text: "Exam has been archived. Student results are preserved.",
        confirmButtonColor: "#5e72e4",
        timer: 2000,
      });

      return { success: true };
    } catch (error) {
      dispatch({
        type: types.ARCHIVE_EXAM_FAILURE,
        payload: error.message,
      });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to archive exam",
        confirmButtonColor: "#5e72e4",
      });

      return { success: false, error: error.message };
    }
  };
};

// ===========================
// STUDENT - EXAM ATTEMPTS
// ===========================

/**
 * Start a new exam attempt
 * Creates an attempt record with randomized question order
 */
export const startExamAttempt = (examId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.CREATE_EXAM_ATTEMPT_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      // Fetch the exam template
      const examRef = doc(db, "fullLengthExams", examId);
      const examDoc = await getDoc(examRef);

      if (!examDoc.exists()) {
        throw new Error("Exam not found");
      }

      const examData = examDoc.data();

      if (!examData.isActive) {
        throw new Error("This exam is not currently available");
      }

      // Fetch questions: embedded (new-style) or from QBank (legacy)
      let activeQuestions = [];

      if (examData.questions && examData.questions.length > 0) {
        // NEW PATH: Embedded questions (uploaded via Excel)
        activeQuestions = examData.questions.map((q) => ({
          ...q,
          answers: q.choices?.map(choice => ({
            answerId: choice.choiceId,
            answerText: choice.text,
            isCorrect: q.correctAnswer?.includes(choice.choiceId)
          })) || [],
          explanation: q.explanationText,
        }));
      } else if (examData.questionIds && examData.questionIds.length > 0) {
        // LEGACY PATH: Fetch from questionBankQuestions
        const questionsRef = collection(db, "questionBankQuestions");
        let allQuestions = [];
        const questionIds = examData.questionIds;

        for (let i = 0; i < questionIds.length; i += 10) {
          const batch = questionIds.slice(i, i + 10);
          const batchQuery = query(questionsRef, where(documentId(), "in", batch));
          const batchSnapshot = await getDocs(batchQuery);
          batchSnapshot.docs.forEach((docSnap) => {
            const questionData = docSnap.data();
            allQuestions.push({
              ...questionData,
              questionId: docSnap.id,
              answers: questionData.choices?.map(choice => ({
                answerId: choice.choiceId,
                answerText: choice.text,
                isCorrect: questionData.correctAnswer?.includes(choice.choiceId)
              })) || [],
              explanation: questionData.explanationText,
            });
          });
        }

        // Filter out archived questions (only relevant for QBank-sourced questions)
        activeQuestions = allQuestions.filter(q => q.status !== 'archived');
      }

      // Attach passage data from exam-level passages array (for embedded exams)
      if (examData.passages?.length > 0 && activeQuestions.length > 0) {
        const examPassageMap = new Map();
        examData.passages.forEach(p => examPassageMap.set(p.passageId, p));
        activeQuestions = activeQuestions.map(q => {
          if (q.passageId && examPassageMap.has(q.passageId)) {
            const p = examPassageMap.get(q.passageId);
            return { ...q, passageTitle: p.title, passageText: p.passageText };
          }
          return q;
        });
      }

      // Fisher-Yates shuffle helper
      const fisherYatesShuffle = (arr) => {
        const shuffled = [...arr];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };

      if (activeQuestions.length === 0) {
        throw new Error("This exam has no available questions. All questions may have been archived.");
      }

      // Strip large content fields for Firestore storage (keeps scoring + analytics fields only)
      const slimQuestion = (q) => ({
        questionId: q.questionId,
        questionCode: q.questionCode || null,
        answers: q.answers.map(a => ({ answerId: a.answerId, isCorrect: a.isCorrect })),
        subjectId: q.subjectId || null,
        subjectName: q.subjectName || null,
        unitId: q.unitId || null,
        unitName: q.unitName || null,
        topicId: q.topicId || null,
        topicName: q.topicName || null,
        passageId: q.passageId || null,
      });

      // Generate attempt ID
      const attemptsRef = collection(db, "fullLengthExamAttempts");
      const attemptQuery = query(
        attemptsRef,
        where("userHandle", "==", userHandle),
        orderBy("attemptNumber", "desc"),
        firestoreLimit(1)
      );
      const attemptSnapshot = await getDocs(attemptQuery);

      let attemptNumber = 1;
      if (!attemptSnapshot.empty) {
        attemptNumber = (attemptSnapshot.docs[0].data().attemptNumber || 0) + 1;
      }

      const attemptId = `ATTEMPT-${userHandle.split("@")[0]}-${Date.now()}`;

      let attemptDocument;
      let fullTestQuestions; // Full question objects for Redux (not saved to Firestore)

      if (examData.hasSections && examData.sections && examData.sections.length >= 2) {
        // SECTION-AWARE PATH: Shuffle within each section independently
        const sectionAttemptData = examData.sections.map((section, idx) => {
          const sectionQuestions = activeQuestions.slice(
            section.questionStartIndex,
            section.questionEndIndex
          );
          const shuffled = fisherYatesShuffle(sectionQuestions);

          return {
            sectionIndex: section.sectionIndex,
            name: section.name,
            questionCount: shuffled.length,
            timeLimitSeconds: section.timeLimitSeconds,
            timeRemaining: section.timeLimitSeconds,
            status: idx === 0 ? "in_progress" : "pending",
            questionIds: shuffled.map((q) => q.questionId),
            startedAt: idx === 0 ? new Date().toISOString() : null,
            completedAt: null,
          };
        });

        // Build flat testQuestions in section order (section 0 shuffled, then section 1 shuffled, etc.)
        const orderedQuestions = sectionAttemptData.flatMap((s) =>
          s.questionIds.map((id) => activeQuestions.find((q) => q.questionId === id))
        );
        fullTestQuestions = orderedQuestions;

        const totalTimeLimit = sectionAttemptData.reduce(
          (sum, s) => sum + s.timeLimitSeconds,
          0
        );

        attemptDocument = {
          attemptId,
          attemptNumber,
          examId,
          examName: examData.name,
          userHandle,
          status: "in_progress",
          questionOrder: orderedQuestions.map((q) => q.questionId),
          testQuestions: orderedQuestions.map(slimQuestion),
          currentQuestionIndex: 0,
          selectedAnswers: {},
          markedQuestions: [],
          questionTimeTracking: {},
          timeLimit: totalTimeLimit,
          timeRemaining: sectionAttemptData[0].timeLimitSeconds,
          totalQuestions: orderedQuestions.length,
          // Section-specific fields
          hasSections: true,
          sections: sectionAttemptData,
          breakDurationSeconds: examData.breakDurationSeconds || 0,
          currentSectionIndex: 0,
          currentSectionQuestionIndex: 0,
          breakState: null,
          startedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
      } else {
        // LEGACY PATH: Global shuffle, 66 seconds per question
        const shuffledQuestions = fisherYatesShuffle(activeQuestions);
        fullTestQuestions = shuffledQuestions;
        const adjustedTimeLimit = shuffledQuestions.length * 66;

        attemptDocument = {
          attemptId,
          attemptNumber,
          examId,
          examName: examData.name,
          userHandle,
          status: "in_progress",
          questionOrder: shuffledQuestions.map((q) => q.questionId),
          testQuestions: shuffledQuestions.map(slimQuestion),
          currentQuestionIndex: 0,
          selectedAnswers: {},
          markedQuestions: [],
          questionTimeTracking: {},
          timeLimit: adjustedTimeLimit,
          timeRemaining: adjustedTimeLimit,
          totalQuestions: shuffledQuestions.length,
          startedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
      }

      const attemptDocRef = doc(attemptsRef, attemptId);
      await setDoc(attemptDocRef, attemptDocument);

      // Dispatch full questions to Redux for immediate use (not the slim Firestore version)
      const savedAttempt = {
        ...attemptDocument,
        testQuestions: fullTestQuestions,
        startedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      dispatch({
        type: types.CREATE_EXAM_ATTEMPT_SUCCESS,
        payload: savedAttempt,
      });

      return { success: true, attemptId, attempt: savedAttempt };
    } catch (error) {
      dispatch({
        type: types.CREATE_EXAM_ATTEMPT_FAILURE,
        payload: error.message,
      });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to start exam",
        confirmButtonColor: "#5e72e4",
      });

      return { success: false, error: error.message };
    }
  };
};

/**
 * Fetch all exam attempts for current user
 */
export const fetchExamAttempts = ({ pageSize = 50, lastDoc = null, append = false } = {}) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.FETCH_EXAM_ATTEMPTS_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      const attemptsRef = collection(db, "fullLengthExamAttempts");
      const constraints = [
        where("userHandle", "==", userHandle),
        orderBy("createdAt", "desc"),
        firestoreLimit(pageSize),
      ];

      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      const q = query(attemptsRef, ...constraints);
      const snapshot = await getDocs(q);

      const attempts = snapshot.docs.map((docSnap) => ({
        attemptId: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toMillis?.() || docSnap.data().createdAt,
        updatedAt: docSnap.data().updatedAt?.toMillis?.() || docSnap.data().updatedAt,
        startedAt: docSnap.data().startedAt?.toMillis?.() || docSnap.data().startedAt,
        completedAt: docSnap.data().completedAt?.toMillis?.() || docSnap.data().completedAt,
      }));

      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;

      dispatch({
        type: types.FETCH_EXAM_ATTEMPTS_SUCCESS,
        payload: {
          attempts,
          append,
          lastVisible,
          hasMore: snapshot.docs.length === pageSize,
        },
      });

      return { success: true, attempts, lastVisible, hasMore: snapshot.docs.length === pageSize };
    } catch (error) {
      dispatch({
        type: types.FETCH_EXAM_ATTEMPTS_FAILURE,
        payload: error.message,
      });

      return { success: false, error: error.message };
    }
  };
};

/**
 * Fetch single exam attempt by ID
 */
export const fetchExamAttempt = (attemptId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.FETCH_EXAM_ATTEMPT_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      const attemptRef = doc(db, "fullLengthExamAttempts", attemptId);
      const attemptDoc = await getDoc(attemptRef);

      if (!attemptDoc.exists()) {
        throw new Error("Exam attempt not found");
      }

      const attemptData = attemptDoc.data();

      // Verify ownership
      if (attemptData.userHandle !== userHandle) {
        throw new Error("Unauthorized access to exam attempt");
      }

      const attempt = {
        attemptId: attemptDoc.id,
        ...attemptData,
        createdAt: attemptData.createdAt?.toMillis?.() || attemptData.createdAt,
        updatedAt: attemptData.updatedAt?.toMillis?.() || attemptData.updatedAt,
        startedAt: attemptData.startedAt?.toMillis?.() || attemptData.startedAt,
        completedAt: attemptData.completedAt?.toMillis?.() || attemptData.completedAt,
      };

      // Hydrate slim questions with full content from exam template
      // Slim format is detected by absence of questionText on the first question
      if (attempt.testQuestions?.length > 0 && !attempt.testQuestions[0].questionText && attempt.examId) {
        try {
          const examRef = doc(db, "fullLengthExams", attempt.examId);
          const examDoc = await getDoc(examRef);

          if (examDoc.exists()) {
            const examData = examDoc.data();
            const fullQuestionsMap = new Map();

            if (examData.questions && examData.questions.length > 0) {
              // Embedded questions path
              examData.questions.forEach((q) => {
                fullQuestionsMap.set(q.questionId, {
                  ...q,
                  answers: q.choices?.map(choice => ({
                    answerId: choice.choiceId,
                    answerText: choice.text,
                    isCorrect: q.correctAnswer?.includes(choice.choiceId)
                  })) || [],
                  explanation: q.explanationText,
                });
              });
            } else if (examData.questionIds && examData.questionIds.length > 0) {
              // Legacy path — fetch from questionBankQuestions
              const questionsRef = collection(db, "questionBankQuestions");
              for (let i = 0; i < examData.questionIds.length; i += 10) {
                const batch = examData.questionIds.slice(i, i + 10);
                const batchQuery = query(questionsRef, where(documentId(), "in", batch));
                const batchSnapshot = await getDocs(batchQuery);
                batchSnapshot.docs.forEach((docSnap) => {
                  const qData = docSnap.data();
                  fullQuestionsMap.set(docSnap.id, {
                    ...qData,
                    questionId: docSnap.id,
                    answers: qData.choices?.map(choice => ({
                      answerId: choice.choiceId,
                      answerText: choice.text,
                      isCorrect: qData.correctAnswer?.includes(choice.choiceId)
                    })) || [],
                    explanation: qData.explanationText,
                  });
                });
              }
            }

            // Merge full content into slim questions, preserving frozen answers from attempt
            attempt.testQuestions = attempt.testQuestions.map((slimQ) => {
              const fullQ = fullQuestionsMap.get(slimQ.questionId);
              if (fullQ) {
                // Merge full content but keep slim answers (frozen correct answers from attempt time)
                const fullAnswers = slimQ.answers.map(slimA => {
                  const fullA = fullQ.answers?.find(a => a.answerId === slimA.answerId);
                  return { ...slimA, answerText: fullA?.answerText || '' };
                });
                return {
                  ...fullQ,
                  ...slimQ,
                  answers: fullAnswers,
                };
              }
              // Question no longer in template — keep slim data (scoring still works)
              return slimQ;
            });

            // Attach passage data from exam-level passages array (for embedded exams)
            if (examData.passages?.length > 0) {
              const examPassageMap = new Map();
              examData.passages.forEach(p => examPassageMap.set(p.passageId, p));
              attempt.testQuestions = attempt.testQuestions.map(q => {
                if (q.passageId && examPassageMap.has(q.passageId)) {
                  const p = examPassageMap.get(q.passageId);
                  return { ...q, passageTitle: p.title, passageText: p.passageText };
                }
                return q;
              });
            }
          }
        } catch (hydrateError) {
          console.error("Failed to hydrate questions from exam template:", hydrateError);
          // Continue with slim questions — scoring still works, display may be limited
        }
      }

      // Hydrate passage data for passage-based questions
      if (attempt.testQuestions?.length > 0) {
        try {
          const uniquePassageIds = [...new Set(
            attempt.testQuestions
              .map(q => q.passageId)
              .filter(Boolean)
          )];

          if (uniquePassageIds.length > 0) {
            const passageMap = new Map();
            const passagesRef = collection(db, "questionBankPassages");

            for (let i = 0; i < uniquePassageIds.length; i += 10) {
              const batch = uniquePassageIds.slice(i, i + 10);
              const batchQuery = query(passagesRef, where(documentId(), "in", batch));
              const batchSnapshot = await getDocs(batchQuery);
              batchSnapshot.docs.forEach((docSnap) => {
                passageMap.set(docSnap.id, { passageId: docSnap.id, ...docSnap.data() });
              });
            }

            // Count questions per passage for metadata
            const passageQuestionCounts = {};
            attempt.testQuestions.forEach(q => {
              if (q.passageId && passageMap.has(q.passageId)) {
                passageQuestionCounts[q.passageId] = (passageQuestionCounts[q.passageId] || 0) + 1;
              }
            });

            // Track passage question numbering
            const passageQuestionIndex = {};
            attempt.testQuestions = attempt.testQuestions.map(q => {
              if (q.passageId && passageMap.has(q.passageId)) {
                passageQuestionIndex[q.passageId] = (passageQuestionIndex[q.passageId] || 0) + 1;
                return {
                  ...q,
                  passageData: passageMap.get(q.passageId),
                  passageQuestionNumber: passageQuestionIndex[q.passageId],
                  totalPassageQuestions: passageQuestionCounts[q.passageId],
                };
              }
              return q;
            });
          }
        } catch (passageError) {
          console.error("Failed to hydrate passage data:", passageError);
        }
      }

      dispatch({
        type: types.FETCH_EXAM_ATTEMPT_SUCCESS,
        payload: attempt,
      });

      return { success: true, attempt };
    } catch (error) {
      dispatch({
        type: types.FETCH_EXAM_ATTEMPT_FAILURE,
        payload: error.message,
      });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to load exam attempt",
        confirmButtonColor: "#5e72e4",
      });

      return { success: false, error: error.message };
    }
  };
};

/**
 * Save exam progress (auto-save during exam)
 */
export const saveExamProgress = (attemptId, progressData) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.SAVE_EXAM_PROGRESS_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      const attemptRef = doc(db, "fullLengthExamAttempts", attemptId);

      // Verify ownership
      const attemptDoc = await getDoc(attemptRef);
      if (!attemptDoc.exists()) {
        throw new Error("Exam attempt not found");
      }

      const attemptData = attemptDoc.data();
      if (attemptData.userHandle !== userHandle) {
        throw new Error("Unauthorized to update this exam attempt");
      }

      const updateData = {
        currentQuestionIndex: progressData.currentQuestionIndex,
        selectedAnswers: progressData.selectedAnswers,
        markedQuestions: progressData.markedQuestions,
        timeRemaining: progressData.timeRemaining,
        questionTimeTracking: progressData.questionTimeTracking || {},
        updatedAt: serverTimestamp(),
        // Section state (only if present)
        ...(progressData.hasSections && {
          currentSectionIndex: progressData.currentSectionIndex,
          currentSectionQuestionIndex: progressData.currentSectionQuestionIndex,
          sections: progressData.sections,
          breakState: progressData.breakState || null,
        }),
      };

      await updateDoc(attemptRef, updateData);

      const updatedAttempt = {
        ...attemptData,
        ...updateData,
        attemptId,
        updatedAt: new Date().toISOString(),
      };

      dispatch({
        type: types.SAVE_EXAM_PROGRESS_SUCCESS,
        payload: updatedAttempt,
      });

      return { success: true };
    } catch (error) {
      dispatch({
        type: types.SAVE_EXAM_PROGRESS_FAILURE,
        payload: error.message,
      });

      // Silent fail for auto-save - don't show error popup
      console.error("Failed to save exam progress:", error.message);

      return { success: false, error: error.message };
    }
  };
};

/**
 * Submit completed exam
 */
export const submitExam = (attemptId, finalData) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.SUBMIT_EXAM_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      const attemptRef = doc(db, "fullLengthExamAttempts", attemptId);

      // Verify ownership
      const attemptDoc = await getDoc(attemptRef);
      if (!attemptDoc.exists()) {
        throw new Error("Exam attempt not found");
      }

      const attemptData = attemptDoc.data();
      if (attemptData.userHandle !== userHandle) {
        throw new Error("Unauthorized to submit this exam");
      }

      // Calculate score
      const { testQuestions, selectedAnswers } = finalData;
      let correctCount = 0;
      let omittedCount = 0;

      testQuestions.forEach((question) => {
        const selectedAnswerId = selectedAnswers[question.questionId];
        if (!selectedAnswerId) {
          omittedCount++;
        } else {
          const correctAnswer = question.answers?.find((a) => a.isCorrect);
          if (selectedAnswerId === correctAnswer?.answerId) {
            correctCount++;
          }
        }
      });

      const score = Math.round((correctCount / testQuestions.length) * 100);

      // Calculate per-section scores if applicable
      let sectionResults = null;
      if (finalData.hasSections && finalData.sections) {
        sectionResults = finalData.sections.map((section) => {
          const sectionQuestionIds = new Set(section.questionIds || []);
          const sectionQuestions = testQuestions.filter((q) =>
            sectionQuestionIds.has(q.questionId)
          );
          let sCorrect = 0;
          let sOmitted = 0;
          sectionQuestions.forEach((q) => {
            const selectedId = selectedAnswers[q.questionId];
            if (!selectedId) {
              sOmitted++;
            } else {
              const correctAnswer = q.answers?.find((a) => a.isCorrect);
              if (selectedId === correctAnswer?.answerId) sCorrect++;
            }
          });
          return {
            sectionIndex: section.sectionIndex,
            name: section.name,
            questionCount: sectionQuestions.length,
            correct: sCorrect,
            incorrect: sectionQuestions.length - sCorrect - sOmitted,
            omitted: sOmitted,
            score: sectionQuestions.length > 0
              ? Math.round((sCorrect / sectionQuestions.length) * 100)
              : 0,
            timeLimitSeconds: section.timeLimitSeconds,
            timeRemaining: section.timeRemaining,
          };
        });
      }

      const updateData = {
        status: "completed",
        selectedAnswers: finalData.selectedAnswers,
        markedQuestions: finalData.markedQuestions,
        timeRemaining: finalData.timeRemaining,
        questionTimeTracking: finalData.questionTimeTracking || {},
        score,
        correctAnswers: correctCount,
        incorrectAnswers: testQuestions.length - correctCount - omittedCount,
        omittedAnswers: omittedCount,
        ...(sectionResults && { sectionResults }),
        ...(finalData.sections && { sections: finalData.sections }),
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(attemptRef, updateData);

      const completedAttempt = {
        ...attemptData,
        ...updateData,
        attemptId,
        completedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      dispatch({
        type: types.SUBMIT_EXAM_SUCCESS,
        payload: completedAttempt,
      });

      return { success: true, score, correctCount, attempt: completedAttempt };
    } catch (error) {
      dispatch({
        type: types.SUBMIT_EXAM_FAILURE,
        payload: error.message,
      });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to submit exam",
        confirmButtonColor: "#5e72e4",
      });

      return { success: false, error: error.message };
    }
  };
};

// ===========================
// ADMIN - ALL EXAM ATTEMPTS (analytics)
// ===========================

/**
 * Fetch all completed exam attempts across all students (admin only)
 * Uses backend with .select() for lightweight response
 */
export const fetchAllExamAttempts = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: types.FETCH_ALL_EXAM_ATTEMPTS_REQUEST });

      const response = await axios.post("/fetchCompletedExamAttempts");
      const attempts = response.data.attempts || [];

      dispatch({
        type: types.FETCH_ALL_EXAM_ATTEMPTS_SUCCESS,
        payload: attempts,
      });

      return { success: true, attempts };
    } catch (error) {
      console.error("Error fetching all exam attempts:", error);

      dispatch({
        type: types.FETCH_ALL_EXAM_ATTEMPTS_FAILURE,
        payload: error.response?.data?.error || error.message,
      });

      return { success: false, error: error.message };
    }
  };
};

// ===========================
// ADMIN - PER-EXAM QUESTION ANALYTICS
// ===========================

/**
 * Fetch question-level analytics for a specific exam (admin)
 * Uses backend server-side aggregation
 */
export const fetchExamQuestionAnalytics = (examId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: types.FETCH_EXAM_QUESTION_ANALYTICS_REQUEST });

      const response = await axios.post("/fetchExamQuestionAnalytics", { examId });
      const payload = response.data;

      dispatch({
        type: types.FETCH_EXAM_QUESTION_ANALYTICS_SUCCESS,
        payload,
      });

      return { success: true, ...payload };
    } catch (error) {
      console.error("Error fetching exam question analytics:", error);
      dispatch({
        type: types.FETCH_EXAM_QUESTION_ANALYTICS_FAILURE,
        payload: error.response?.data?.error || error.message,
      });
      return { success: false, error: error.message };
    }
  };
};

// ===========================
// EXAM HISTORY (for analytics)
// ===========================

/**
 * Fetch all completed attempts for a specific exam by current user
 * Used for historical comparison in analytics
 */
export const fetchExamAttemptHistory = (examId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.FETCH_EXAM_HISTORY_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error("User not authenticated");
      }

      const attemptsRef = collection(db, "fullLengthExamAttempts");
      const q = query(
        attemptsRef,
        where("userHandle", "==", userHandle),
        where("examId", "==", examId),
        where("status", "==", "completed"),
        orderBy("completedAt", "asc")
      );

      const snapshot = await getDocs(q);

      const history = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          attemptId: docSnap.id,
          attemptNumber: data.attemptNumber,
          examId: data.examId,
          examName: data.examName,
          score: data.score,
          correctAnswers: data.correctAnswers,
          incorrectAnswers: data.incorrectAnswers,
          omittedAnswers: data.omittedAnswers,
          totalQuestions: data.totalQuestions,
          timeLimit: data.timeLimit,
          timeRemaining: data.timeRemaining,
          completedAt: data.completedAt?.toMillis?.() || data.completedAt,
        };
      });

      dispatch({
        type: types.FETCH_EXAM_HISTORY_SUCCESS,
        payload: history,
      });

      return { success: true, history };
    } catch (error) {
      console.error("Error fetching exam history:", error);

      dispatch({
        type: types.FETCH_EXAM_HISTORY_FAILURE,
        payload: error.message,
      });

      return { success: false, error: error.message };
    }
  };
};

// ===========================
// ADMIN - ENHANCED ANALYTICS (with user profiles + in-progress)
// ===========================

/**
 * Fetch ALL attempts with enriched user profiles (admin)
 * Uses backend with denormalized user data
 */
export const fetchEnhancedAnalyticsData = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: types.FETCH_ENHANCED_ANALYTICS_REQUEST });

      const response = await axios.post("/fetchEnhancedExamAnalytics");
      const { allAttempts, completionRate } = response.data;

      dispatch({
        type: types.FETCH_ENHANCED_ANALYTICS_SUCCESS,
        payload: {
          allAttempts: allAttempts || [],
          completionRate: completionRate || {},
        },
      });

      return { success: true };
    } catch (error) {
      console.error("Error fetching enhanced analytics:", error);
      dispatch({ type: types.FETCH_ENHANCED_ANALYTICS_FAILURE, payload: error.response?.data?.error || error.message });
      return { success: false, error: error.message };
    }
  };
};

// ===========================
// ADMIN - STUDENT EXAM DETAILS (for modal)
// ===========================

/**
 * Fetch detailed exam history for a specific student (admin)
 * Uses cached backend
 */
export const fetchStudentExamDetails = (userHandle) => {
  return async (dispatch) => {
    try {
      dispatch({ type: types.FETCH_STUDENT_EXAM_DETAILS_REQUEST });

      const response = await axios.post("/fetchStudentExamDetails", { userHandle });
      const payload = response.data;

      dispatch({ type: types.FETCH_STUDENT_EXAM_DETAILS_SUCCESS, payload });

      return { success: true, data: payload };
    } catch (error) {
      console.error("Error fetching student exam details:", error);
      dispatch({ type: types.FETCH_STUDENT_EXAM_DETAILS_FAILURE, payload: error.response?.data?.error || error.message });
      return { success: false, error: error.message };
    }
  };
};

// ===========================
// CLEAR/RESET ACTIONS
// ===========================

export const clearCurrentExam = () => ({
  type: types.CLEAR_CURRENT_EXAM,
});

export const clearCurrentAttempt = () => ({
  type: types.CLEAR_CURRENT_ATTEMPT,
});

export const clearExamHistory = () => ({
  type: types.CLEAR_EXAM_HISTORY,
});

export const clearEnhancedAnalytics = () => ({
  type: types.CLEAR_ENHANCED_ANALYTICS,
});

export const clearStudentExamDetails = () => ({
  type: types.CLEAR_STUDENT_EXAM_DETAILS,
});

export const resetExamState = () => ({
  type: types.RESET_EXAM_STATE,
});
